// frontend/src/services/api.js
import axios from 'axios';
import csrfTokenService from './CsrfTokenService';
import { useAuthStore } from '@/stores/auth'; // Import for use in interceptor
// import router from '@/router'; // To redirect on 401

const apiClient = axios.create({
  baseURL: 'https://bagusphere.com/rentapi/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Store CSRF token globally on the apiClient instance or in Pinia store
// This function will be called by AuthService after login or status check.
export const setCsrfTokenHeader = (token) => {
  if (token) {
    apiClient.defaults.headers.common['X-CSRFToken'] = token;
    // console.log("token: ", token);
    // console.log("apiClient.defaults.headers.common['X-CSRFToken']: ", apiClient.defaults.headers.common['X-CSRFToken']);
    // console.log('CSRF token set on apiClient defaults.');
  } else {
    delete apiClient.defaults.headers.common['X-CSRFToken'];
    // console.log('CSRF token cleared from apiClient defaults.');
  }
};

apiClient.interceptors.request.use(
  (config) => {
    // Check if user has logged out and prevent API calls
    const hasLoggedOut = sessionStorage.getItem('user_logged_out');
    if (hasLoggedOut && !config.url.includes('/auth/login') && !config.url.includes('/auth/register') && !config.url.includes('/auth/csrf-token')) {
      console.warn('Blocking API call - user has logged out');
      return Promise.reject(new Error('User has logged out'));
    }

    // Additional check: if we're making a request to a protected endpoint but auth store shows not logged in
    const authStore = useAuthStore();
    const isProtectedEndpoint = !config.url.includes('/auth/') && !config.url.includes('/captcha/');
    if (isProtectedEndpoint && !authStore.isLoggedIn) {
      console.warn('Blocking API call - user not authenticated for protected endpoint');
      return Promise.reject(new Error('User not authenticated'));
    }

    const mutatingMethods = ['post', 'put', 'delete', 'patch'];
    if (mutatingMethods.includes(config.method.toLowerCase())) {
      const csrfToken = apiClient.defaults.headers.common['X-CSRFToken'];
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      } else {
        // This warning is important. The token should have been fetched by AuthService.
        console.warn('CSRF token not found in apiClient defaults for a mutating request. ' +
                     'Ensure AuthService.login() or authStore.fetchUserStatus() sets it.');
        // Optionally, you could try to fetch it here as a last resort, but it's better if pre-fetched.
      }
    }

    console.groupCollapsed("Axios Request");
    // console.log("URL:", config.baseURL + config.url);
    // console.log("Method:", config.method);
    // console.log("Headers:", config.headers);
    // console.log("With Credentials:", config.withCredentials);
    // console.log("Cookies:", document.cookie);
    console.groupEnd();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for logging and CSRF token expiry handling
apiClient.interceptors.response.use(
  response => {
    console.groupCollapsed(
      `🟢 Axios Response: ${response.config.method.toUpperCase()} ${response.config.url}`
    );
    // console.log("Status:", response.status, response.statusText);
    // console.log("Response Headers:", response.headers);
    // console.log("Response Data:", response.data);
    // console.log("Browser Cookies after response:", document.cookie);
    console.groupEnd();
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // Check if this is a CSRF token expiry error
    if (csrfTokenService.isCsrfExpiredError(error) && !originalRequest._retry) {
      console.log('🔄 CSRF token expired, handling with CsrfTokenService...');
      
      try {
        const result = await csrfTokenService.handleCsrfExpiry(originalRequest);
        if (result) {
          return result;
        }
      } catch (refreshError) {
        console.error('❌ Error during CSRF token refresh:', refreshError);
      }
    }

    // Handle other authentication errors
    if (await csrfTokenService.handleAuthError(error)) {
      return Promise.reject(error);
    }

    // Handle other errors
    if (error.response) {
      console.groupCollapsed(
        `🔴 Axios Error Response: ${error.config.method.toUpperCase()} ${error.config.url}`
      );
      // console.log("Status:", error.response.status, error.response.statusText);
      // console.log("Response Headers:", error.response.headers);
      // console.log("Response Data:", error.response.data);
      // console.log("Browser Cookies after error response:", document.cookie);
      console.groupEnd();
    } else {
      console.error("Axios Error (no response):", error.message);
    }
    return Promise.reject(error);
  }
);
export default apiClient;
